import type { OfferExtraModel, OfferModel } from '@/types/api-models'
import type {
	AdditionalService,
	ReservationPriceBreakdown,
	ReservationSummaryLine,
} from '@/types/reservation'
import { differenceInDays } from 'date-fns'

const AFTER_HOUR_EXTRA_ID = 'APH'

export function getReservationDays(pickupDate?: string, dropoffDate?: string) {
	if (!pickupDate || !dropoffDate) return 1

	const days = differenceInDays(new Date(dropoffDate), new Date(pickupDate))

	if (Number.isNaN(days)) return 1

	return Math.max(1, days)
}

function normalizeTaxRate(taxRate?: number) {
	return typeof taxRate === 'number' && taxRate > 0 ? taxRate : 0
}

function roundCurrency(amount: number) {
	return Math.round(amount)
}

export function getAdditionalServiceUnitPriceWithTax(
	service: Pick<AdditionalService, 'price' | 'taxRate'>,
) {
	return roundCurrency(service.price * (1 + normalizeTaxRate(service.taxRate)))
}

export function getAdditionalServiceTotalPrice(
	service: AdditionalService,
	days: number,
) {
	const safeDays = Math.max(1, days)
	const unitPriceWithTax = getAdditionalServiceUnitPriceWithTax(service)

	return service.priceType === 'daily'
		? unitPriceWithTax * safeDays
		: unitPriceWithTax
}

export function buildAdditionalServiceSummaryLines(
	services: AdditionalService[] | undefined,
	days: number,
): ReservationSummaryLine[] {
	if (!services?.length) return []

	const safeDays = Math.max(1, days)

	return services
		.filter(service => service.selected)
		.map(service => ({
			id: service.id,
			label:
				service.priceType === 'daily'
					? `${service.name} (${safeDays} ${safeDays === 1 ? 'día' : 'días'})`
					: service.name,
			amount: getAdditionalServiceTotalPrice(service, safeDays),
			type: 'extra' as const,
		}))
		.filter(line => line.amount > 0)
}

function findAfterHourExtra(
	allExtras: OfferExtraModel[] | undefined,
): OfferExtraModel | undefined {
	return allExtras?.find(
		e => String(e.extraID).toUpperCase() === AFTER_HOUR_EXTRA_ID,
	)
}

/**
 * Costo unitario del recargo After Hour.
 * Fuente: campo `value` del extra con extraID = "APH" en `allExtras` de la oferta.
 * Fallback: `otherTaxValue` por compatibilidad.
 */
export function getAfterHoursSurchargeAmount(
	offer?: Pick<OfferModel, 'otherTaxValue' | 'allExtras'> | null,
) {
	const extra = findAfterHourExtra(offer?.allExtras)
	if (extra) return Math.max(0, roundCurrency(extra.value))
	return Math.max(0, roundCurrency(offer?.otherTaxValue || 0))
}

export function buildAfterHoursSurchargeLines(
	unitAmount: number,
	isPickupAfterHours: boolean,
	isDropoffAfterHours: boolean,
): ReservationSummaryLine[] {
	const amount = Math.max(0, roundCurrency(unitAmount))
	if (amount === 0) return []
	const count = (isPickupAfterHours ? 1 : 0) + (isDropoffAfterHours ? 1 : 0)
	if (count === 0) return []
	return [
		{
			id: '__surcharge_after_hours__',
			label: `After Hour x ${count}`,
			amount: amount * count,
			type: 'surcharge',
		},
	]
}

interface ReservationPriceBreakdownOptions {
	vehicleTotal: number
	services?: AdditionalService[]
	days: number
	discountAmount?: number
	surchargeLines?: ReservationSummaryLine[]
}

export function buildReservationPriceBreakdown({
	vehicleTotal,
	services,
	days,
	discountAmount = 0,
	surchargeLines = [],
}: ReservationPriceBreakdownOptions): ReservationPriceBreakdown {
	const serviceLines = buildAdditionalServiceSummaryLines(services, days)
	const additionalLines = [...serviceLines, ...surchargeLines]
	const extrasTotal = serviceLines.reduce((sum, line) => sum + line.amount, 0)
	const afterHoursSurcharge = surchargeLines.reduce(
		(sum, line) => sum + line.amount,
		0,
	)
	const addOnsTotal = extrasTotal + afterHoursSurcharge
	const subtotal = Math.max(0, roundCurrency(vehicleTotal) + addOnsTotal)
	const normalizedDiscount = Math.max(0, roundCurrency(discountAmount))

	return {
		vehicleTotal: Math.max(0, roundCurrency(vehicleTotal)),
		additionalLines,
		extrasTotal,
		afterHoursSurcharge,
		addOnsTotal,
		discountAmount: normalizedDiscount,
		subtotal,
		total: Math.max(0, subtotal - normalizedDiscount),
	}
}

export function calculateAdditionalServicesTotal(
	services: AdditionalService[] | undefined,
	days: number,
) {
	return buildAdditionalServiceSummaryLines(services, days).reduce(
		(sum, line) => sum + line.amount,
		0,
	)
}
